<template>
  <header class="omx-pagehead" :class="{ 'is-sticky': sticky, 'has-lightbox': lightbox }">
    <div class="omx-pagehead__top">
      <div
          v-if="avatar || avatarFallback || $slots.avatar"
          class="omx-pagehead__avatar"
          :class="{ 'is-clickable': !!avatar }"
          :role="avatar ? 'button' : null"
          :tabindex="avatar ? 0 : null"
          :title="avatar ? $t('common.image') : null"
          @click="avatar && (lightbox = true)"
          @keydown.enter="avatar && (lightbox = true)"
      >
        <slot name="avatar">
          <img v-if="avatar" :src="avatar" :alt="title" />
          <span v-else class="omx-pagehead__avatar-initials">{{ avatarFallback }}</span>
        </slot>
      </div>

      <transition name="omx-lightbox">
        <div v-if="lightbox" class="omx-pagehead__lightbox" role="dialog" :aria-label="title" @click.self="lightbox = false">
          <button class="omx-pagehead__lightbox-close" :aria-label="$t('common.cancel')" @click="lightbox = false">
            <b-icon icon="close" />
          </button>
          <img :src="avatar" :alt="title" />
        </div>
      </transition>
      <div class="omx-pagehead__title-row">
        <div class="omx-pagehead__pills" v-if="pills && pills.length">
          <span
              v-for="(p, i) in pills"
              :key="i"
              class="omx-pagehead__pill"
              :class="pillClass(p)"
          >
            <span class="omx-pagehead__pill-dot" v-if="p.dot !== false"></span>
            {{ p.label }}
          </span>
        </div>
        <h1 class="omx-pagehead__title">
          <slot name="title">{{ title }}</slot>
        </h1>
        <p v-if="subtitle || $slots.subtitle" class="omx-pagehead__subtitle">
          <slot name="subtitle">{{ subtitle }}</slot>
        </p>
        <div v-if="$slots.meta" class="omx-pagehead__meta">
          <slot name="meta" />
        </div>
      </div>
      <div class="omx-pagehead__actions" v-if="$slots.actions">
        <slot name="actions" />
      </div>
    </div>
    <div v-if="$slots.tabs" class="omx-pagehead__tabs">
      <slot name="tabs" />
    </div>
  </header>
</template>

<script>
/**
 * OmxPageHeader — sticky detail-page header.
 *
 *   <OmxPageHeader
 *       :title="project.title"
 *       :subtitle="`${project.code} · ${project.site}`"
 *       :pills="[
 *         { label: $t('phase.execution'), tone: 'live' },
 *         { label: 'P-1042', tone: 'draft', dot: false }
 *       ]"
 *       sticky
 *   >
 *     <template #meta>
 *       <span><b>Managers</b> J. Dupont · M. Lefèvre</span>
 *     </template>
 *     <template #actions>
 *       <b-button @click="back" icon-left="arrow-left">Back</b-button>
 *       <b-button type="is-primary" icon-left="pencil" @click="edit">Edit</b-button>
 *     </template>
 *     <template #tabs>
 *       <OmxTab ... />
 *     </template>
 *   </OmxPageHeader>
 *
 * Pill tones: 'live' (green), 'warn' (amber), 'risk' (red), 'draft' (brand),
 * 'neutral' (default).
 */
export default {
  name: "OmxPageHeader",
  props: {
    title:    { type: String, default: null },
    subtitle: { type: String, default: null },
    pills:    { type: Array,  default: () => [] },
    sticky:   { type: Boolean, default: false },
    // Leading avatar: image URL, with initials shown until/unless one exists.
    avatar:         { type: String, default: null },
    avatarFallback: { type: String, default: null }
  },
  data() {
    return { lightbox: false };
  },
  watch: {
    lightbox(open) {
      if (open) window.addEventListener('keydown', this.onLightboxKey);
      else window.removeEventListener('keydown', this.onLightboxKey);
    },
    // If the image is swapped/removed while open (e.g. edit preview), close.
    avatar(val) {
      if (!val) this.lightbox = false;
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onLightboxKey);
  },
  methods: {
    pillClass(p) {
      const tone = p.tone || 'neutral';
      return [`omx-pagehead__pill--${tone}`];
    },
    onLightboxKey(e) {
      if (e.key === 'Escape') this.lightbox = false;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-pagehead {
  background: $omx-surface;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-lg;
  padding: 18px 22px;
  margin-bottom: 16px;

  &.is-sticky {
    position: sticky;
    top: $omx-shell-top-height;
    z-index: 10;
    box-shadow: $omx-shadow-sm;
  }

  // While the lightbox is open, lift the header's stacking context above
  // the shell (sidebar is z-index 30) so the fixed overlay covers everything.
  &.has-lightbox {
    z-index: 130;
  }
}

.omx-pagehead__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
}

.omx-pagehead__title-row {
  flex: 1;
  min-width: 0;
}

.omx-pagehead__avatar {
  // Full-bleed: cancel the header card's padding so the image fills the
  // whole height and sits flush with the left edge. The image is absolutely
  // positioned so the avatar never adds height — the text content alone
  // dictates the header height, and the image stretches to match it.
  align-self: stretch;
  position: relative;
  margin: -18px 0 -18px -22px;
  width: 96px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: $omx-radius-lg 0 0 $omx-radius-lg;
  background: $omx-brand-50;
  border-right: 1px solid $omx-ink-100;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.omx-pagehead__avatar-initials {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: .02em;
  color: $omx-brand-700;
}

.omx-pagehead__avatar.is-clickable {
  cursor: zoom-in;
  transition: filter .12s;
  &:hover img { filter: brightness(1.06); }
  &:focus-visible {
    outline: 2px solid $omx-brand-500;
    outline-offset: -2px;
  }
}

.omx-pagehead__lightbox {
  position: fixed;
  inset: 0;
  z-index: 140;
  background: rgba(11, 18, 32, .72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  cursor: zoom-out;

  img {
    max-width: min(92vw, 1100px);
    max-height: 86vh;
    border-radius: $omx-radius-lg;
    box-shadow: $omx-shadow-lg;
    cursor: default;
  }
}
.omx-pagehead__lightbox-close {
  position: absolute;
  top: 18px;
  right: 22px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, .25);
  background: rgba(255, 255, 255, .1);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255, 255, 255, .22); }
}

.omx-lightbox-enter-active,
.omx-lightbox-leave-active {
  transition: opacity .16s ease;
  img { transition: transform .16s ease; }
}
.omx-lightbox-enter,
.omx-lightbox-leave-to {
  opacity: 0;
  img { transform: scale(.96); }
}

.omx-pagehead__title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.012em;
  margin: 0 0 2px;
  color: $omx-ink-900;
}

.omx-pagehead__subtitle {
  margin: 0;
  color: $omx-ink-500;
  font-size: 13px;
}

.omx-pagehead__pills {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.omx-pagehead__pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  background: $omx-ink-100;
  color: $omx-ink-700;

  &--live   { background: $omx-success-100; color: $omx-success-600; }
  &--warn   { background: $omx-warn-100;    color: $omx-warn-600; }
  &--risk   { background: $omx-danger-100;  color: $omx-danger-600; }
  &--draft  { background: $omx-brand-100;   color: $omx-brand-700; }
  &--neutral { background: $omx-ink-100;    color: $omx-ink-700; }
}

.omx-pagehead__pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.omx-pagehead__meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
  color: $omx-ink-500;
  margin-top: 10px;

  ::v-deep b {
    color: $omx-ink-700;
    font-weight: 600;
    margin-right: 4px;
  }
}

.omx-pagehead__actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.omx-pagehead__tabs {
  margin-top: 16px;
  border-top: 1px solid $omx-ink-100;
  padding-top: 4px;
  margin-left: -22px;
  margin-right: -22px;
  margin-bottom: -18px;
  padding-left: 6px;
  padding-right: 6px;
}

@media (max-width: 768px) {
  .omx-pagehead {
    padding: 14px 16px;
  }
  .omx-pagehead__top {
    flex-direction: column;
    gap: 12px;
  }
  .omx-pagehead__avatar {
    // Column layout: full-width banner strip across the top of the card.
    margin: -14px -16px 0;
    width: calc(100% + 32px);
    aspect-ratio: auto;
    height: 140px;
    border-radius: $omx-radius-lg $omx-radius-lg 0 0;
    border-right: none;
    border-bottom: 1px solid $omx-ink-100;
  }
}
</style>
